import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'pt';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    pt: 'Equipa'
  },
  'nav.matches': {
    en: 'Matches',
    pt: 'Jogos'
  },
  'nav.deals': {
    en: 'Deals',
    pt: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    pt: 'Resultados Ao Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    pt: 'Compra a Tua'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of Portugal 2026",
    pt: 'Agora fazes parte de Portugal 2026'
  },
  'hero.title1': {
    en: "Don't Just Support Portugal,",
    pt: 'Não Apenas Apoies Portugal,'
  },
  'hero.title2': {
    en: 'Live It',
    pt: 'Vive-o'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    pt: 'Atualizado em tempo real. Acede a conteúdo exclusivo, atualizações de jogos e ofertas de viagem diretamente do teu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    pt: 'Ver Jogos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    pt: 'Explorar Equipa'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    pt: 'Desliza para descobrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    pt: 'O Nosso Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    pt: 'Uma Nação Forjada'
  },
  'team.title2': {
    en: 'In Glory',
    pt: 'Na Glória'
  },
  'team.desc': {
    en: 'The Portuguese national team represents more than just football; it embodies the passion, resilience, and artistry of a nation. From the historic triumph in Paris to conquering the inaugural Nations League, A Seleção continues to write its name in the stars.',
    pt: 'A seleção portuguesa representa mais do que futebol; incorpora a paixão, resiliência e arte de uma nação. Do triunfo histórico em Paris à conquista da Nations League inaugural, A Seleção continua a escrever o seu nome nas estrelas.'
  },
  'team.since': {
    en: 'Since 1921',
    pt: 'Desde 1921'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    pt: 'Explorar a Nossa História Completa →'
  },
  'team.euro': {
    en: 'Euro 2016',
    pt: 'Euro 2016'
  },
  'team.euroDesc': {
    en: 'Champions of Europe',
    pt: 'Campeões da Europa'
  },
  'team.nationsLeague': {
    en: 'Nations League',
    pt: 'Liga das Nações'
  },
  'team.nationsLeagueDesc': {
    en: '2019 & 2025 Winners',
    pt: 'Vencedores 2019 & 2025'
  },
  'team.euros': {
    en: '8 Euros',
    pt: '8 Euros'
  },
  'team.eurosDesc': {
    en: 'Championship Appearances',
    pt: 'Participações em Europeus'
  },
  'team.worldCups': {
    en: '8 World Cups',
    pt: '8 Mundiais'
  },
  'team.worldCupsDesc': {
    en: 'Global Stage Appearances',
    pt: 'Participações no Palco Mundial'
  },
  'team.videoLabel': {
    en: 'Sangue Português',
    pt: 'Sangue Português'
  },
  'team.videoTitle1': {
    en: 'One Nation, One',
    pt: 'Uma Nação, Um'
  },
  'team.videoTitle2': {
    en: 'Heartbeat',
    pt: 'Batimento'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    pt: 'O Plantel'
  },
  'players.title1': {
    en: 'The Stars of',
    pt: 'As Estrelas de'
  },
  'players.title2': {
    en: 'Portugal',
    pt: 'Portugal'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    pt: 'Ver Plantel Completo na ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    pt: 'Avançado'
  },
  'players.midfielder': {
    en: 'Midfielder',
    pt: 'Médio'
  },
  'players.defender': {
    en: 'Defender',
    pt: 'Defesa'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    pt: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    pt: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    pt: 'Jogos'
  },
  'matches.desc': {
    en: 'Follow A Seleção in Group K. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    pt: 'Acompanha A Seleção no Grupo K. Obtém atualizações ao vivo, onzes oficiais e resultados em tempo real do maior palco do futebol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    pt: 'Classificação do Grupo'
  },
  'matches.live': {
    en: 'Live',
    pt: 'Ao Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    pt: 'Atualiza automaticamente a cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    pt: 'Atualizar Agora'
  },
  'matches.team': {
    en: 'Team',
    pt: 'Equipa'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    pt: 'Ver Classificação Ao Vivo no FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    pt: 'Próximo Jogo'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    pt: 'Calendário ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    pt: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    pt: 'FlashScore Ao Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    pt: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'Portugal By The Numbers',
    pt: 'Portugal em Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    pt: 'Participações em Mundiais'
  },
  'stats.titles': {
    en: 'Major Titles',
    pt: 'Títulos Principais'
  },
  'stats.goals': {
    en: 'International Goals',
    pt: 'Golos Internacionais'
  },
  'stats.ronaldo': {
    en: 'Ronaldo Goals (Record)',
    pt: 'Golos de Ronaldo (Recorde)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    pt: 'Os Arquivos'
  },
  'history.title1': {
    en: 'Moments That',
    pt: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    pt: 'Fizeram História'
  },
  'history.euro2016': {
    en: 'Euro 2016 Final Victory',
    pt: 'Vitória na Final do Euro 2016'
  },
  'history.eder': {
    en: "Éder's Historic Goal",
    pt: 'O Golo Histórico de Éder'
  },
  'history.ronaldoGoals': {
    en: "Ronaldo's All FIFA World Cup Goals",
    pt: 'Todos os Golos de Ronaldo em Mundiais FIFA'
  },
  'history.nationsLeague': {
    en: 'Nations League 2025 Triumph',
    pt: 'Triunfo na Liga das Nações 2025'
  },
  'history.nationsLeague2019': {
    en: 'Nations League 2019 — The First',
    pt: 'Liga das Nações 2019 — A Primeira'
  },
  'history.ronaldoVsSpain': {
    en: "Ronaldo's Hat-Trick vs Spain",
    pt: 'Hat-Trick de Ronaldo contra Espanha'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    pt: 'Mais Que Um Jogo'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    pt: 'Sente a Paixão de'
  },
  'fan.title2': {
    en: 'Portugal',
    pt: 'Portugal'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey to global glory.',
    pt: 'Junta-te a milhões de vozes cantando como uma só. Veste as cores, sente a história e faz parte da jornada para a glória mundial.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    pt: 'Viaja para o Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    pt: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    pt: 'para Fãs'
  },
  'deals.desc': {
    en: 'Exclusive discounts for Portugal fans on tickets, hotels, flights & transport to support A Seleção in North America.',
    pt: 'Descontos exclusivos para fãs de Portugal em bilhetes, hotéis, voos e transporte para apoiar A Seleção na América do Norte.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    pt: 'Desbloqueia Descontos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    pt: 'Aplica o código no checkout dos sites parceiros'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    pt: 'Bilhetes para Jogos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    pt: 'Até 15% de desconto em pacotes da fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    pt: 'Encontrar Bilhetes'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    pt: 'Hotéis & Alojamento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    pt: 'Ofertas de hotéis parceiros perto dos estádios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    pt: 'Reservar Hotéis'
  },
  'deals.flights': {
    en: 'Flights',
    pt: 'Voos'
  },
  'deals.flightsDesc': {
    en: 'Special fares from Lisbon & Porto to USA',
    pt: 'Tarifas especiais de Lisboa e Porto para os EUA'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    pt: 'Pesquisar Voos'
  },
  'deals.transport': {
    en: 'Local Transport',
    pt: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    pt: 'Serviços de shuttle e aluguer de carros nas cidades dos jogos'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    pt: 'Planear Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for Portugal 2026',
    pt: 'Tudo o Que Precisas para Portugal 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    pt: 'QG do'
  },
  'fanHq.title2': {
    en: 'HQ',
    pt: 'Adepto'
  },
  'fanHq.countdown': {
    en: "Countdown to Portugal's First Match",
    pt: 'Contagem Decrescente para o Primeiro Jogo de Portugal'
  },
  'fanHq.days': {
    en: 'Days',
    pt: 'Dias'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    pt: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    pt: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    pt: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    pt: 'Capacidade'
  },
  'fanHq.chants': {
    en: 'Learn the',
    pt: 'Aprende os'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    pt: 'Cânticos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    pt: 'Hino Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    pt: 'Cântico Clássico de Estádio'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    pt: 'Favorito da Multidão'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    pt: 'Cântico de Vitória'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    pt: 'Ver no YouTube'
  },
  'fanHq.phrases': {
    en: 'Essential',
    pt: 'Frases'
  },
  'fanHq.phrasesHighlight': {
    en: 'Phrases',
    pt: 'Essenciais'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    pt: 'Links'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    pt: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    pt: 'Onde Assistir'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    pt: 'Canais de streaming e TV'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    pt: 'Canal de Info para Fãs'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    pt: 'Informação oficial FIFA para fãs'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    pt: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    pt: 'Ranking mundial oficial'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    pt: 'Calendário Completo do Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    pt: 'Todos os 104 jogos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for Portugal 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    pt: 'Um produto digital oficial da Qtap by STDI. O teu hub de T-shirt NFC inteligente para Portugal 2026 — dados de jogos em tempo real, conteúdo exclusivo e ofertas de viagem ao teu alcance.'
  },
  'footer.resources': {
    en: 'Resources',
    pt: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    pt: 'Equipa'
  },
  'footer.firstTeam': {
    en: 'First Team',
    pt: 'Equipa Principal'
  },
  'footer.matches': {
    en: 'Matches',
    pt: 'Jogos'
  },
  'footer.standings': {
    en: 'Standings',
    pt: 'Classificação'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    pt: 'Ofertas de Viagem'
  },
  'footer.support': {
    en: 'Support',
    pt: 'Suporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    pt: 'Contacta-nos'
  },
  'footer.faq': {
    en: 'FAQ',
    pt: 'FAQ'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    pt: 'Política de Privacidade'
  },
  'footer.terms': {
    en: 'Terms of Service',
    pt: 'Termos de Serviço'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    pt: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    pt: 'Qtap by STDI. Todos os direitos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => prev === 'en' ? 'pt' : 'en');
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-211">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}